import { Result } from "../../interfaces/Result";
import { createEventEntry } from "./EventEntry";
import { Barn, Contact, EventClass, EventEntry, EventFee, Horse, Membership, Owner, Rider, Trainer } from "../../models";
import {  CreateEventClassEntryInput, CreateEventEntryInput } from "../../API";
import { getRiderAndHorseActiveMemberships } from "../membership/EventEntryMembership";
import constants from "../../constant/constant";
import { EventEntryStatusOptions } from "./EventEntryStatus";
import { formattedOption, formattedOptionGroup, OptionGroupsSortedByDiscipline } from "../../components/EventClass/ClassInfoSection";
import { createEventClassEntry } from "../eventClassEntry/EventClassEntry";
import { getEventFeesByEventId } from "../eventFee/EventFee";
import { createEventEntryFee } from "../eventEntryFee/EventEntryFee";
import { addRequiredEventEntryFeesByEventByEntry } from "../eventEntryFee/AddRequiredEventEntryFees";

export interface CreateEntryInterface {
    userId: string,
    eventId: string,
    organizationId: string,
    currentBarn?: Barn
    currentHorse?: Horse,
    currentOwner?: Owner,
    currentRider?: Rider,
    currentRider2?: Rider,
    currentTrainer?: Trainer,
    currentContact?: Contact,
    payerName?: string,
    disciplinesWithSelectedValues?: OptionGroupsSortedByDiscipline[],
    isIncomplete?: boolean
}

export const blankEntry: CreateEntryInterface = {
    userId: "",
    eventId: "",
    organizationId: ""
};

export async function createNewEventEntry(input: CreateEntryInterface): Promise<Result> {
    let backNumber: number | undefined = undefined;
   
    // For RHSC, try to match the entry to an active membership and auto-assign a back number
    if (input.organizationId === constants.RHSC_ORGANIZATION.id) {
        if (input.currentRider && input.currentHorse) {
            const membershipList: Membership[] = await getRiderAndHorseActiveMemberships(input.currentRider, input.currentHorse, input.organizationId);
            if (membershipList && membershipList.length > 0) {
                for (let i = 0; i < membershipList.length; i++) {
                    const mem = membershipList[i];
                    if (mem.backNumber) {
                        backNumber = mem.backNumber;
                        break;
                    }
                }
            }
        }
    }

    const divisionList = input.disciplinesWithSelectedValues ? createDivisionListForEntry(input.disciplinesWithSelectedValues) : [];
    
    // create entry with status = in_progress
    const eventEntryInput: CreateEventEntryInput = {
        createdBy: input.userId,
        eventId: input.eventId,
        status: input.isIncomplete ? EventEntryStatusOptions.inProgress : EventEntryStatusOptions.complete,
        number: backNumber,
        personId: input.userId,
        contactId: input.currentContact?.id || "", 
        riderId: input.currentRider?.id, 
        riderName: input.currentRider?.name, 
        horseId: input.currentHorse?.id, 
        horseName: input.currentHorse?.name, 
        ownerId: input.currentOwner?.id, 
        ownerName: input.currentOwner?.name, 
        trainerId: input.currentTrainer?.id,
        trainerName: input.currentTrainer?.name,
        barnName: input.currentBarn?.name,
        barnId: input.currentBarn?.id,
        secondRiderId: input.currentRider2?.id, 
        secondRiderName: input.currentRider2?.name,
        payerName: input.payerName,
        divisionList:  divisionList
    };
    const entryResult = await createEventEntry(eventEntryInput);
    if (entryResult.isSuccess) {
        const currentEntry: EventEntry = entryResult.result;
        // Create the classes for this entry
        await createClassesForEntry(input, currentEntry);

        // Add the required event entry fees for this entry
        await addRequiredEventEntryFeesByEventByEntry(input.userId, input.eventId, currentEntry);

        let result: Result = {isSuccess: true, type: "EventEntry", result: currentEntry, message: "Successfully created the event entry!"};
        return result;
    } else {
        let result: Result = {isSuccess: false, type: "EventEntry", result: entryResult.result, message: "Could not create the entry. Please contact hello@ringsidepro.com for help."};
        return result;
    }
}

export const createDivisionListForEntry = (disciplinesWithSelectedValues: OptionGroupsSortedByDiscipline[]) => {
    const divisionIdList: string[] = [];
    // Loop through the disciplines
    for (let i = 0; i < disciplinesWithSelectedValues.length; i++) {
        const discipline: OptionGroupsSortedByDiscipline = disciplinesWithSelectedValues[i];
        if (discipline.includesOneOrMoreSelectedClasses) {
            const optionGroupArray: formattedOptionGroup[] = discipline.optionGroupArray;
            // Loop through the optionGroups in this discipline
            for (let j = 0; j < optionGroupArray.length; j++) {
                const optionGroup: formattedOptionGroup = optionGroupArray[j];
                if (optionGroup.division && optionGroup.division.isChecked && optionGroup.division.object.id) {
                    divisionIdList.push(optionGroup.division.object.id)
                }
            }
        }
    }
    return divisionIdList;
}

const createClassesForEntry = async (input: CreateEntryInterface, eventEntry: EventEntry) => {
    const disciplinesWithSelectedValues = input.disciplinesWithSelectedValues;
    if (disciplinesWithSelectedValues) {
        // Loop through the disciplines
        for (let i = 0; i < disciplinesWithSelectedValues.length; i++) {
            const discipline: OptionGroupsSortedByDiscipline = disciplinesWithSelectedValues[i];
            if (discipline.includesOneOrMoreSelectedClasses) {
                const optionGroupArray: formattedOptionGroup[] = discipline.optionGroupArray;
                // Loop through the optionGroups in this discipline
                for (let j = 0; j < optionGroupArray.length; j++) {
                    const optionGroup: formattedOptionGroup = optionGroupArray[j];
                    
                    // Check if this is a stand-alone class
                    if (optionGroup.class && optionGroup.class.isChecked) {
                        const formattedOption: formattedOption = optionGroup.class;
                        const createResult = await createEventClassEntryFromFormattedOptionGroup(input, eventEntry, formattedOption);
                        if (!createResult.isSuccess) {
                            console.error("Could not create the EventClassEntry: ", createResult);
                        }
                    } 
                    // Or classes that are part of a division
                    else if (optionGroup.classes) {
                        for (let k = 0; k < optionGroup.classes.length; k++) {
                            const currentFormattedOption: formattedOption = optionGroup.classes[k];
                            if (currentFormattedOption.isChecked) {
                                const createResult = await createEventClassEntryFromFormattedOptionGroup(input, eventEntry, currentFormattedOption);
                                if (!createResult.isSuccess) {
                                    console.error("Could not create the EventClassEntry: ", createResult);
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}
export const createEventClassEntryFromFormattedOptionGroup = async (input: CreateEntryInterface, eventEntry: EventEntry, formattedOption: formattedOption) => {
    const currentClass: EventClass = formattedOption?.object;
    const eventClassEntryInput: CreateEventClassEntryInput = {
        createdBy: input.userId,
        lastUpdatedBy: input.userId,
        eventId: input.eventId,
        eventClassId: (currentClass.id || ""),
        eventEntryId: eventEntry.id,
        riderId: formattedOption.selectedRider?.id || input.currentRider?.id || "",
        status: EventEntryStatusOptions.complete //TO DO: Check if event auto-accepts entries
    };
    const createResult = await createEventClassEntry(eventClassEntryInput);
    return createResult;
}